import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Link2, Trash2 } from 'lucide-react';
import type { Enlace, Nodo } from '@doc/shared';
import { DIAS_VIGENCIA } from '@doc/shared';
import { api } from '../../lib/api.js';
import { fecha } from '../../lib/formato.js';
import { Modal } from '../../ui/Modal.js';
import { useAvisos } from '../../ui/avisos.js';

/**
 * Compartir por enlace.
 *
 * Todo lo que se ve acá está redactado para que nadie comparta por accidente:
 * un enlace salta la lista de permisos entera, así que la pantalla lo dice con
 * todas sus letras y empuja a poner caducidad.
 */
export function DialogoCompartir({ nodo, alCerrar }: { nodo: Nodo; alCerrar(): void }) {
  const cliente = useQueryClient();
  const { avisar, avisarError } = useAvisos();
  const [dias, setDias] = useState<number | null>(30);
  const [permiteDescarga, setPermiteDescarga] = useState(true);
  const [nota, setNota] = useState('');
  const [copiado, setCopiado] = useState<string | null>(null);

  const { data: enlaces, isLoading } = useQuery({
    queryKey: ['enlaces', nodo.id],
    queryFn: () => api<Enlace[]>(`/nodos/${nodo.id}/enlaces`),
  });

  const refrescar = () => cliente.invalidateQueries({ queryKey: ['enlaces', nodo.id] });

  const crear = useMutation({
    mutationFn: () =>
      api<Enlace>(`/nodos/${nodo.id}/enlaces`, {
        metodo: 'POST',
        cuerpo: { diasVigencia: dias, permiteDescarga, nota: nota.trim() || null },
      }),
    onSuccess: (enlace) => {
      void refrescar();
      void copiar(enlace.url);
      setNota('');
      avisar('Enlace creado y copiado al portapapeles');
    },
    onError: avisarError,
  });

  const revocar = useMutation({
    mutationFn: (id: string) => api(`/enlaces/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void refrescar();
      avisar('Enlace revocado: deja de funcionar de inmediato');
    },
    onError: avisarError,
  });

  async function copiar(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(url);
      setTimeout(() => setCopiado(null), 2500);
    } catch {
      // Sin permiso de portapapeles (o sin HTTPS) queda el campo para copiar a mano.
      avisar('Copia el enlace manualmente desde el campo', 'aviso');
    }
  }

  return (
    <Modal
      titulo={`Compartir "${nodo.nombre}"`}
      icono={<Link2 size={17} style={{ color: 'var(--green-light)' }} />}
      ancho
      alCerrar={alCerrar}
      pie={
        <button className="btn" onClick={alCerrar}>
          Cerrar
        </button>
      }
    >
      <div className="aviso-compartir">
        <AlertTriangle size={16} />
        <div>
          <strong>Cualquiera con el enlace entra, sin cuenta ni contraseña.</strong>
          <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
            {nodo.tipo === 'CARPETA'
              ? 'Verá esta carpeta y lo que contiene, nada más: no puede subir al resto del repositorio.'
              : 'Verá únicamente este archivo.'}{' '}
            El acceso queda registrado en la bitácora.
          </div>
        </div>
      </div>

      <div className="campo">
        <label>Caducidad</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {DIAS_VIGENCIA.map((d) => (
            <button key={d} className={`btn btn--sm${dias === d ? ' btn--primario' : ''}`} onClick={() => setDias(d)}>
              {d === 365 ? '1 año' : `${d} días`}
            </button>
          ))}
          <button className={`btn btn--sm${dias === null ? ' btn--primario' : ''}`} onClick={() => setDias(null)}>
            Sin caducidad
          </button>
        </div>
        {dias === null && (
          <span className="aviso-ambar" style={{ fontSize: 11.5 }}>
            Un enlace sin caducidad vive para siempre: tendrás que acordarte de revocarlo.
          </span>
        )}
      </div>

      <div className="campo">
        <label>Permisos del enlace</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, textTransform: 'none' }}>
          <input
            className="check"
            type="checkbox"
            checked={permiteDescarga}
            onChange={(e) => setPermiteDescarga(e.target.checked)}
          />
          Permitir descargar los archivos
        </label>
        <span className="muted" style={{ fontSize: 11 }}>
          Sin esto solo se pueden ver en pantalla. No es una barrera infranqueable —quien ve, puede fotografiar— pero
          evita el reenvío cómodo del archivo original.
        </span>
      </div>

      <div className="campo">
        <label>Nota (opcional)</label>
        <input
          className="input"
          placeholder="Para qué o para quién es este enlace"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          maxLength={200}
        />
      </div>

      <button className="btn btn--primario" onClick={() => crear.mutate()} disabled={crear.isPending}>
        <Link2 size={14} /> {crear.isPending ? 'Creando…' : 'Crear enlace'}
      </button>

      <div style={{ marginTop: 22 }}>
        <h4 style={{ fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--dim)', margin: '0 0 8px' }}>
          Enlaces activos
        </h4>

        {isLoading && <div className="cargando">Cargando…</div>}
        {enlaces?.length === 0 && (
          <p className="muted" style={{ fontSize: 12.5 }}>
            Todavía no compartiste este {nodo.tipo === 'CARPETA' ? 'carpeta' : 'archivo'}.
          </p>
        )}

        {enlaces?.map((e) => (
          <div key={e.id} className="enlace">
            <div style={{ minWidth: 0, flex: 1 }}>
              <input className="input enlace__url" readOnly value={e.url} onFocus={(ev) => ev.target.select()} />
              <div className="enlace__meta">
                {e.expiraEn ? `Caduca el ${fecha(e.expiraEn)}` : 'Sin caducidad'} ·{' '}
                {e.permiteDescarga ? 'permite descarga' : 'solo lectura en pantalla'} · {e.visitas} visita(s)
                {e.ultimaVisita && ` · última ${fecha(e.ultimaVisita)}`}
                {e.nota && ` · ${e.nota}`}
              </div>
            </div>
            <button className="btn btn--sm" onClick={() => void copiar(e.url)} title="Copiar enlace">
              {copiado === e.url ? <Check size={14} /> : <Copy size={14} />}
            </button>
            <button
              className="btn btn--sm btn--peligro"
              onClick={() => revocar.mutate(e.id)}
              disabled={revocar.isPending}
              title="Revocar: el enlace deja de funcionar"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
